// Reads Misti's own reply text and picks the emotion her face should wear while
// she says it.
//
// Her voice (Sarvam bulbul) has no emotion controls, so Misti is prompted to
// carry her feelings in her words: she opens a reply with a short feeling word -
// "Oh no,", "Yay!", "Hmm,", "Uff," - which the voice performs naturally. That
// opening word is the clearest signal of how she feels, so it is checked first
// and wins outright; the rest of the rules read the sentence itself.
//
// Only HER text is classified, never the guest's. The guest saying "no sorry, one
// more naan" must not make Misti look apologetic; she is not the one apologising.
//
// English, Hindi (Devanagari and romanised) and Bengali (Bengali script and
// romanised), because that is how guests - and therefore the LLM - actually talk,
// often mixing all three in one sentence.
//
// Rules are ordered by priority. The first match wins, so the more specific and
// more important reading (an apology) beats a generic one (a question) - "Sorry,
// could you say that again?" is an apology that happens to end in a question mark.

// Hindi and Bengali STT - and the LLM answering in those languages - write
// common English loanwords in native script: सॉरी, थैंक यू, সরি, থ্যাংক ইউ. People
// say them constantly, so every rule carries those spellings as well as the
// native words and the romanised forms.

// Bad news: something the guest wanted cannot be had - sold out, not
// available. She is sad about it, and it shows, even when she also says sorry.
const BAD_NEWS = [
	/\b(unfortunately|afraid (we|i)|not available|unavailable|out of stock|sold out|we (don'?t|do not) have|we'?re out of|run out|all gone|none left)\b/i,
	// Indian English for sold out: "the mango lassi is finished today". Only
	// "X is finished", never "are you finished?".
	/\b(it'?s|is|are) (all |already )?finished\b/i,
	/\b(khatam ho (gaya|gayi|gya)|khatm ho (gaya|gayi)|available nahi|nahi milega|shesh hoye geche|pawa jachhe na|paowa jachhe na)\b/i,
	w(["उपलब्ध नहीं", "ख़त्म हो गया", "खत्म हो गया", "ख़त्म हो गई", "खत्म हो गई", "नहीं मिलेगा"]),
	w(["পাওয়া যাচ্ছে না", "শেষ হয়ে গেছে", "আর নেই", "পাওয়া যাবে না"]),
];

// Serving: the food, the drink, the table is here. Her serving smile.
const SERVING = [
	/\b(here('?s| is| are) your|there you go|here you go|bon app[eé]tit|enjoy your (meal|food|drink|coffee|tea|stay|evening)|your (order|food|meal|coffee|tea|drink|dish|table|room)('?s| is| are) (ready|here))\b/i,
	/\b(aapka (order|khana) (aa gaya|ready hai|taiyar hai)|apnar (khabar|order) (eshe geche|ready))\b/i,
	w(["आपका ऑर्डर तैयार", "आपका ऑर्डर आ गया", "आपका खाना आ गया", "आपका खाना तैयार", "खाने का आनंद", "लीजिए आपका"]),
	w(["আপনার খাবার এসে গেছে", "আপনার অর্ডার এসে গেছে", "আপনার অর্ডার তৈরি", "খাবার উপভোগ করুন", "এই নিন আপনার"]),
];

const APOLOGY = [
	/\b(sorry|apologi[sz]e|apologies)\b/i,
	/\b(maaf|maafi|kshama|dukkhito|dukhito|mafi|sori)\b/i,
	w(["माफ़", "माफ", "क्षमा", "खेद", "सॉरी", "सॉरि"]),
	w(["দুঃখিত", "মাফ", "ক্ষমা", "সরি", "স্যরি"]),
];

// The feeling words she opens a reply with - the same list her Dograh prompt
// gives her (keep the two in step). English, romanised, Hindi and Bengali.
export const OPENERS = {
	cry: ["boo hoo", "boohoo", "sniff sniff", "sniff", "sob", "rona aa gaya", "kanna pachhe", "सुबक", "रोना आ गया", "কান্না পাচ্ছে"],
	sad: ["oh no", "oh dear", "awww", "aww", "aw", "sadly", "arre nahi", "aha re", "ish", "ओह नो", "अरे नहीं", "अफ़सोस", "अफसोस", "ইশ", "ইস", "আহা রে", "আহারে", "হায় রে"],
	frustrated: ["ugh", "ughh", "uff", "ufff", "oof", "hmph", "humph", "argh", "grr", "grrr", "dhur", "dhyat", "उफ़", "उफ्फ", "उफ", "हुंह", "উফ", "উফফ", "ধুর", "ধ্যাৎ"],
	apologetic: ["oops", "whoops", "my bad", "उप्स", "ऊप्स", "উপস"],
	excited: ["yay", "yayy", "woohoo", "woo hoo", "hooray", "yippee", "wow", "oh wow", "hi", "hello", "hey", "wah", "waah", "arre wah", "वाह", "अरे वाह", "याय", "यिप्पी", "বাহ", "বাঃ", "ইয়ে"],
	surprised: ["whoa", "woah", "oh my", "arre", "oma", "अरे", "ওমা", "আরে"],
	delighted: ["haha", "hahaha", "ha ha", "हाहा", "हा हा", "हाहाहा", "হাহা", "হা হা", "হাহাহা"],
	shy: ["hehe", "hee hee", "teehee", "tee hee", "हीही", "ही ही", "হিহি", "হি হি"],
	curious: ["hmm", "hmmm", "ooh", "हम्म", "হুম", "হুমম"],
	happy: ["great", "awesome", "बढ़िया", "बहुत बढ़िया", "দারুণ"],
	smile: ["here you go", "there you go", "here it is", "yes", "sure", "of course", "absolutely", "okay", "ok", "लीजिए", "ये लीजिए", "ज़रूर", "जरूर", "बिलकुल", "बिल्कुल", "ठीक है", "এই নিন", "নিন", "অবশ্যই", "নিশ্চয়ই", "ঠিক আছে"],
};

// Every opener, longest first, so a phrase is never beaten by a shorter one it
// starts with: "oh no" before "oh"-anything, "arre wah" before "arre".
const OPENER_LIST = Object.entries(OPENERS)
	.flatMap(([emotion, words]) => words.map((word) => [word.normalize("NFC").toLowerCase(), emotion]))
	.sort((x, y) => y[0].length - x[0].length);

const LETTER = /[\p{L}\p{M}]/u;

// The feeling word at the very start of the text, if there is one - after any
// quotes, asterisks or dashes ("*sniff*").
function opener(t) {
	const head = t.replace(/^[\s"'“”‘’(*\-–—.…]+/, "").toLowerCase();
	for (const [word, emotion] of OPENER_LIST) {
		// A whole word: "aw" must not match "awful", nor "hi" "Hindi".
		if (head.startsWith(word) && !LETTER.test(head.charAt(word.length))) return { emotion, word };
	}
	return null;
}

const QUESTION = /[?？]\s*["'”’)]*\s*$/;

const any = (patterns, t) => patterns.some((p) => p.test(t));

const RULES = [
	{
		// "Sorry, could you say that again?" / "Sorry, did you say two or three?"
		// She did not catch something and is asking. Drooping sad eyes are far too
		// heavy for that - she is puzzled, not sorry. Checked BEFORE the apology
		// rule, but only when nothing in the sentence is bad news, so "Sorry, we're
		// out of paneer - would you like dal?" still gets the sad face.
		emotion: "confused",
		reason: "asking the guest to clarify",
		test: (t) =>
			!any(BAD_NEWS, t) &&
			(any(
				[
					/\b(didn'?t (catch|get|understand)|did not (catch|get|understand)|could you (repeat|say that again)|say that again|pardon|not sure what you mean|come again|did you say)\b/i,
					/\b(samajh nahi|samjha nahi|bujhte parini|bujhlam na|abar bolun|phir se boliye)\b/i,
					w(["समझ नहीं", "समझा नहीं", "फिर से बोलिए", "दोबारा बोलिए", "पार्डन"]),
					w(["বুঝতে পারিনি", "বুঝলাম না", "আবার বলুন", "আবার বলবেন"]),
				],
				t,
			) ||
				(any(APOLOGY, t) && QUESTION.test(t))),
	},
	{
		// "Sorry, we're out of paneer" is sad before it is sorry: the guest has
		// lost something they wanted, and her face shares that.
		emotion: "sad",
		reason: "bad news",
		patterns: BAD_NEWS,
	},
	{
		emotion: "apologetic",
		reason: "apology",
		patterns: APOLOGY,
	},
	{
		emotion: "grateful",
		reason: "thanks",
		patterns: [
			// "thank" alone would match "thanksgiving menu"; require the phrase.
			/\b(thank you|thanks( a lot| so much)?|thankyou|much appreciated)\b/i,
			/\b(dhanyavaad|dhanyavad|dhanyabad|dhonnobad|shukriya|shukria)\b/i,
			w(["धन्यवाद", "शुक्रिया", "थैंक यू", "थैंक्यू", "थैंक यु", "थैंक्स"]),
			w(["ধন্যবাদ", "অনেক ধন্যবাদ", "থ্যাংক ইউ", "থ্যাঙ্ক ইউ", "থ্যাংকস", "থ্যাংক্স"]),
		],
	},
	{
		// Before "excited": "you're welcome" answers a thank-you and deserves a
		// warm smile, not the big wide-eyed greeting that "Welcome!" gets.
		emotion: "happy",
		reason: "acknowledging thanks",
		patterns: [
			/\b(you'?re welcome|you are welcome|my pleasure|no problem|happy to help|anytime)\b/i,
			/\b(koi baat nahi|koi baat nahin|kono somossa nei|kono byapar na)\b/i,
			// Not "आपका स्वागत है" / "আপনাকে স্বাগত": English "you're welcome" answers a
			// thank-you, but these mean "welcome to you" - a greeting, handled below.
			w(["कोई बात नहीं", "नो प्रॉब्लम"]),
			w(["কোনো সমস্যা নেই", "কোনো ব্যাপার না", "নো প্রবলেম"]),
		],
	},
	{
		emotion: "excited",
		reason: "greeting or welcome",
		patterns: [
			// "hi there", never bare "hi": romanised Hindi uses "hi" as an everyday
			// emphatic particle - "bahut hi tasty", "ek hi" - which is no greeting.
			/\b(welcome|hello|hi there|hey there|namaste|namaskar|nomoshkar|good (morning|afternoon|evening))\b/i,
			w(["नमस्ते", "नमस्कार", "स्वागत", "हेलो", "हैलो", "वेलकम"]),
			w(["নমস্কার", "স্বাগতম", "স্বাগত", "হ্যালো", "হেলো", "ওয়েলকাম"]),
		],
	},
	{
		// Before "delighted": "Here's your biryani - it's delicious!" is a serving
		// moment first.
		emotion: "smile",
		reason: "serving",
		patterns: SERVING,
	},
	{
		emotion: "delighted",
		reason: "recommendation or enthusiasm",
		patterns: [
			/\b(delicious|tasty|amazing|wonderful|excellent|fantastic|perfect|great choice|you'?ll love|highly recommend|our (best|most popular))\b/i,
			/\b(badhiya|zabardast|shandar|darun|khub bhalo|osadharon)\b/i,
			w(["बढ़िया", "ज़बरदस्त", "जबरदस्त", "शानदार", "स्वादिष्ट", "मज़ेदार", "टेस्टी"]),
			w(["দারুণ", "খুব ভালো", "অসাধারণ", "সুস্বাদু", "চমৎকার", "টেস্টি"]),
		],
	},
	{
		// A warm farewell gets the grateful bow, NOT heart eyes. Heart eyes and a
		// big blush from a female-voiced robot, aimed at departing families, can
		// read as flirtatious in an Indian restaurant. "love" stays available for a
		// person driving her by teleop, but nothing she says triggers it by itself.
		emotion: "grateful",
		reason: "warm goodbye",
		patterns: [
			/\b(have a (lovely|wonderful|great|nice) (day|evening|meal|night)|come again|see you (soon|again)|take care|goodbye|bye|good night)\b/i,
			/\b(phir milenge|abar asben|abar dekha hobe|alvida|shubh ratri|subho ratri)\b/i,
			w(["फिर मिलेंगे", "फिर आइए", "अलविदा", "शुभ रात्रि", "बाय", "गुड नाइट"]),
			w(["আবার আসবেন", "আবার দেখা হবে", "ভালো থাকবেন", "শুভ রাত্রি", "বাই", "গুড নাইট"]),
		],
	},
	{
		emotion: "curious",
		reason: "asking the guest something",
		// Question mark at the END - "Paneer? Yes, we have it." is not a question
		// from Misti. Hindi and Bengali both use "?"; "？" is the full-width form
		// some keyboards produce.
		patterns: [QUESTION],
	},
];

// Matches any of `words` as a whole word in Indic scripts. \b does not work on
// Devanagari/Bengali (their combining marks are not \w), so bound the match on
// "not a letter or combining mark" instead.
function w(words) {
	const alt = words.map((x) => x.normalize("NFC").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
	return new RegExp(`(?:^|[^\\p{L}\\p{M}])(?:${alt})(?:$|[^\\p{L}\\p{M}])`, "u");
}

// Numbers and currency make a line look like a price list, where "thanks" or
// "welcome" is almost never the point ("Welcome drink 120, thali 350").
const PRICE_LIST = /(₹|rs\.?|inr|\brupees?\b)\s*\d|\d+\s*(₹|rs\.?|rupees?)|(\d[\d,]*\s*[,/|]\s*){2,}/i;

/**
 * @param {string} text  Misti's reply (or a chunk of it)
 * @returns {{emotion: string, reason: string, opener: boolean} | null}
 *   `opener` is true when the emotion came from the feeling word she opened
 *   with - her deliberate choice, which the face should show outright.
 */
export function classify(text) {
	if (!text) return null;
	// One spelling of each letter: "ज़" and "য়" can arrive precomposed or as a
	// letter plus a dot, depending on the keyboard, the STT or the LLM. They look
	// the same and must match the same.
	const t = String(text).normalize("NFC").trim();
	if (!t) return null;

	const open = opener(t);
	if (open) return { emotion: open.emotion, reason: `opens with "${open.word}"`, opener: true };

	const priceList = PRICE_LIST.test(t);

	for (const rule of RULES) {
		if (priceList && (rule.emotion === "grateful" || rule.emotion === "excited")) continue;
		const hit = rule.test ? rule.test(t) : rule.patterns.some((p) => p.test(t));
		if (hit) return { emotion: rule.emotion, reason: rule.reason, opener: false };
	}
	return null;
}

